import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  Box,
  FormControlLabel,
  InfoIcon,
  Radio,
  RadioGroup,
  Tooltip
} from 'edenred-ui';
import { useController, useFormContext } from 'react-hook-form';

import {
  FundsLoadingTypes,
  LimitType,
  LUNCH
} from '@epi-constants/benefitTypes';
import { toComma } from '@epi-helpers/commaHelper';
import { BenefitSettingsForm } from '@epi-models/containers/ManageBenefitGroups';
import { AutomaticRepeatCheckbox } from '@epi-containers/ManageBenefitGroups/components/AutomaticRepeatCheckbox';
import { DailyLimitField } from '@epi-containers/ManageBenefitGroups/components/DailyLimitField';

import { BenefitHeader } from './BenefitHeader';
import { Bold, Italic } from './BenefitSettingsModal.styles';

type OwnProps = {
  maxAmounts: number[];
  index: number;
};

const BenefitSettingsLunch = ({ maxAmounts, index }: OwnProps) => {
  const { sizeMedium, primaryFrontColor, fontSemiBold } = useTheme();
  const { control, setValue, trigger } = useFormContext<BenefitSettingsForm>();
  const { t } = useTranslation();

  const {
    field: { value: isMax, onChange: setIsMax }
  } = useController({
    name: `groups.${index}.isMaximumSpendingLimit`,
    control,
    defaultValue: LimitType.maximum
  });

  const {
    field: { value: fundsLoadingType, onChange: setFundsLoadingType }
  } = useController({
    name: `groups.${index}.fundsLoadingType`,
    rules: { required: true },
    control
  });

  const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === LimitType.maximum) {
      setValue(
        `groups.${index}.maxAmountPerPeriod`,
        toComma(Number(maxAmounts[0]).toFixed(2))
      );
    }

    setIsMax(e.target.value);
    trigger(`groups.${index}.maxAmountPerPeriod`);
  };

  return (
    <Box
      border="1px solid #E0E0E0"
      padding="24px"
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <BenefitHeader benefitName={LUNCH} />
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Box
              fontSize={sizeMedium}
              fontWeight={fontSemiBold}
              id="lounari-loading-type-title"
            >
              {t('components.manage_benefits.spending_limit_field.title')}
            </Box>

            <Tooltip
              arrow
              placement="top"
              componentsProps={{
                tooltip: { sx: { maxWidth: '500px' } },
                popper: { sx: { zIndex: 7000 } }
              }}
              title={
                <Trans
                  i18nKey="components.manage_benefits.spending_limit_field.info"
                  components={{ bold: <Bold />, italic: <Italic /> }}
                />
              }
            >
              <Box pl={1} id="lounari-loading-type-info-icon">
                <InfoIcon color={primaryFrontColor} />
              </Box>
            </Tooltip>
          </Box>
          <RadioGroup onChange={handleLimitChange} value={isMax}>
            <FormControlLabel
              value={LimitType.maximum}
              control={<Radio />}
              label={t(
                'components.manage_benefits.spending_limit_field.labels.maximum'
              )}
            />
            <FormControlLabel
              value={LimitType.custom}
              control={<Radio />}
              label={t(
                'components.manage_benefits.spending_limit_field.labels.custom'
              )}
            />
          </RadioGroup>

          <Box display="flex" alignItems="center" gap={1}>
            <DailyLimitField
              label={t(
                `components.manage_benefit_${LUNCH.toLowerCase()}.detail_limit_label`
              )}
              readOnly={isMax === LimitType.maximum}
              benefitType={LUNCH.toLowerCase()}
              options={[]}
              name={`groups.${index}.maxAmountPerPeriod`}
              fundsLoadingTypeName={`groups.${index}.fundsLoadingType`}
              maxAmounts={maxAmounts}
            />

            <Tooltip
              arrow
              placement="top"
              id={`lounari-limit-section-limit-tooltip-${LUNCH.toLowerCase()}`}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: '500px'
                  }
                },
                popper: {
                  sx: { zIndex: 7000 }
                }
              }}
              title={
                <Trans
                  i18nKey={`components.manage_benefit_${LUNCH.toLowerCase()}.detail_daily_limit_statement`}
                />
              }
            >
              <Box
                pl={1}
                pt={2}
                pr={2.5}
                id={`lounari-limit-section-limit-tooltip-icon-${LUNCH.toLowerCase()}`}
              >
                <InfoIcon color={primaryFrontColor} />
              </Box>
            </Tooltip>
          </Box>
        </Box>

        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Box
              fontSize={sizeMedium}
              fontWeight={fontSemiBold}
              id={`lounari-loading-type-title-${LUNCH}`}
            >
              {t('components.manage_benefits.loading_type_field.title')}
            </Box>

            <Tooltip
              arrow
              placement="top"
              componentsProps={{
                tooltip: { sx: { maxWidth: '500px' } },
                popper: { sx: { zIndex: 7000 } }
              }}
              title={
                <Trans i18nKey="components.manage_benefits.loading_type_field.lunch_info" />
              }
            >
              <Box pl={1} id="lounari-loading-type-info-icon">
                <InfoIcon color={primaryFrontColor} />
              </Box>
            </Tooltip>
          </Box>

          <RadioGroup
            value={fundsLoadingType}
            onChange={e => setFundsLoadingType(e.target.value)}
          >
            <FormControlLabel
              value={FundsLoadingTypes.TopUpRequest}
              control={<Radio />}
              label={t(
                'components.manage_benefits.loading_type_field.labels.top_up_request'
              )}
            />
            <FormControlLabel
              value={FundsLoadingTypes.DirectLoad}
              control={<Radio />}
              label={t(
                'components.manage_benefits.loading_type_field.labels.direct_load'
              )}
            />
          </RadioGroup>
          {fundsLoadingType === FundsLoadingTypes.TopUpRequest && (
            <AutomaticRepeatCheckbox
              disabled={false}
              name={`groups.${index}.recurringTopupRequestEnabled`}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BenefitSettingsLunch;
